import React, { useEffect, useLayoutEffect, useMemo, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Loading } from '@/ui/components/atoms/Loading';
import { ToggleSwitch } from '@/ui/components/atoms/ToggleSwitch';
import { ControlsContainer } from '@/ui/components/molecules/ControlsContainer';
import { TrackerChannelCard } from './components/TrackerChannelCard';
// Note: AffaireTodoModal and TodoUrgencyModal removed - "À faire" is now based on tracker_communication_metadata
import { StatusGroupFilter, StatusGroupFilterHeader } from './components/StatusGroupFilter';
import { useTrackerList } from './hooks/useTrackerList';
import { useChannelSubscription } from './hooks/useChannelSubscription';
import { useStatusGroupRules } from './hooks/useStatusGroupRules';
import { useTrackerUnreadCount } from './hooks/useTrackerUnreadCount';
import { trackerAPI } from './api/tracker.api';
import { getStatusGroupId, STATUS_GROUPS } from './constants/statusGroups.constants';
import { useAuth } from '@/core/auth/AuthContext';
import './styles/TrackerTab.css';

/**
 * TrackerTab - List view for all tracker channels
 * Clicking a channel navigates to /admin/tracker/:affaireId for details
 */
export const TrackerTab: React.FC = () => {
  const { user } = useAuth();
  const location = useLocation();

  // Track last clicked affaire for highlighting (from navigation state)
  const [lastClickedAffaireId, setLastClickedAffaireId] = useState<number | null>(
    (location.state as { lastClickedAffaireId?: number })?.lastClickedAffaireId ?? null
  );

  // Track if we've already scrolled to the highlighted card (to avoid re-scrolling on data refresh)
  const hasScrolledToCard = useRef(false);

  // Clear highlight after 3 seconds
  useEffect(() => {
    if (lastClickedAffaireId) {
      const timer = setTimeout(() => setLastClickedAffaireId(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [lastClickedAffaireId]);

  const {
    channels,
    allChannels,
    isLoading,
    isSearching,
    isSearchMode,
    error,
    filters,
    updateFilter,
    refetch,
    uniqueResponsables,
    pagination,
    todoAffaireIds
  } = useTrackerList();

  const { subscribe, unsubscribe, isLoading: subscriptionLoading } = useChannelSubscription();

  // Evaluate status group rules for all channels
  const { ruleEvaluations, criticalCount, warningCount } = useStatusGroupRules(channels);

  // Get unread affaire IDs for notification badges
  const { unreadAffaireIds, refetch: refetchUnreadCount } = useTrackerUnreadCount();

  // Scroll to highlighted card when returning from detail page
  // Uses useLayoutEffect for synchronous execution before paint
  useLayoutEffect(() => {
    if (hasScrolledToCard.current || lastClickedAffaireId === null) return;

    // Poll for the card element - it may not be in DOM yet
    let attempts = 0;
    const maxAttempts = 20; // Max ~200ms of polling

    const tryScroll = () => {
      const card = document.querySelector(`[data-affaire-id="${lastClickedAffaireId}"]`);
      if (card) {
        hasScrolledToCard.current = true;
        card.scrollIntoView({ behavior: 'instant', block: 'center' });
        return;
      }

      attempts++;
      if (attempts < maxAttempts) {
        requestAnimationFrame(tryScroll);
      }
    };

    tryScroll();
  }, [lastClickedAffaireId]);

  // Apply "non lu" filter to channels if enabled
  const filteredChannels = useMemo(() => {
    if (!filters.showUnreadOnly) return channels;
    return channels.filter(channel => unreadAffaireIds.has(channel.affaire_id));
  }, [channels, filters.showUnreadOnly, unreadAffaireIds]);

  // Status filter expanded state
  const [statusFilterExpanded, setStatusFilterExpanded] = useState(true);

  // Note: Todo modal state removed - "À faire" is now based on tracker_communication_metadata

  // Clearing notification state
  const [clearingNotificationAffaireId, setClearingNotificationAffaireId] = useState<number | null>(null);

  // Bulk action loading states
  const [isBulkMarkingRead, setIsBulkMarkingRead] = useState(false);
  const [isBulkMarkingUnread, setIsBulkMarkingUnread] = useState(false);
  const [markingUnreadAffaireId, setMarkingUnreadAffaireId] = useState<number | null>(null);

  // Pre-filter channels by active filter and search (but NOT status) for accurate status counts
  const channelsBeforeStatusFilter = useMemo(() => {
    return allChannels.filter(channel => {
      // Apply search filter
      if (filters.searchQuery.trim()) {
        const query = filters.searchQuery.toLowerCase();
        const matchesSearch =
          channel.name?.toLowerCase().includes(query) ||
          channel.affaire_id?.toString().includes(query) ||
          channel.adresse_rue_bien?.toLowerCase().includes(query) ||
          channel.commune_bien?.toLowerCase().includes(query) ||
          channel.description?.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }

      // Apply active filter (all/subscribed/my_affaires/a_faire/specific_user)
      switch (filters.activeFilter) {
        case 'subscribed':
          return channel.is_subscribed;
        case 'my_affaires':
          // Match current user's fm_name with responsable_name
          return channel.responsable_name && user?.fmName && channel.responsable_name === user.fmName;
        case 'a_faire':
          // Only include channels with todo items
          return todoAffaireIds.has(channel.affaire_id);
        case 'specific_user':
          // Match selected responsable
          return filters.selectedResponsableName
            ? channel.responsable_name === filters.selectedResponsableName
            : false;
        case 'all':
        default:
          return true;
      }
    });
  }, [allChannels, filters.searchQuery, filters.activeFilter, filters.selectedResponsableName, user, todoAffaireIds]);

  // Calculate channel counts by status group (from filtered channels, excluding status filter)
  const channelsByGroup = useMemo(() => {
    const map = new Map<number, number>();

    for (const channel of channelsBeforeStatusFilter) {
      const groupId = getStatusGroupId(channel.status);
      // getStatusGroupId now returns 999 for unmapped statuses, so groupId should always be defined
      // unless channel.status is null/undefined
      if (groupId !== undefined) {
        map.set(groupId, (map.get(groupId) || 0) + 1);
      }
    }

    return map;
  }, [channelsBeforeStatusFilter]);

  // Calculate total visible channels for status filter
  const totalVisibleChannels = useMemo(() => {
    return Array.from(channelsByGroup.entries())
      .filter(([groupId]) => filters.visibleStatusGroups.length === 0 || filters.visibleStatusGroups.includes(groupId))
      .reduce((sum, [, count]) => sum + count, 0);
  }, [channelsByGroup, filters.visibleStatusGroups]);

  // Sort options for ControlsContainer
  const sortOptions = [
    { value: 'urgency', label: 'Priorité (urgent → bas)' },
    { value: 'lateness', label: 'Urgence (jours en retard)' },
    { value: 'last_activity_date', label: 'Dernière activité' },
    { value: 'created_at', label: 'Date de création' },
    { value: 'status', label: 'Statut' },
    { value: 'name', label: 'Nom' },
    { value: 'affaire_id', label: 'ID Affaire' },
    { value: 'unread_count', label: 'Non lues' },
    { value: 'active_count', label: 'Actives' }
  ];

  // Calculate notification counts per filter based on unread affaires
  const filterNotificationCounts = useMemo(() => {
    const counts: Record<string, number> = {
      my_affaires: 0,
      a_faire: 0,
      subscribed: 0,
      all: 0,
      specific_user: 0
    };

    // For each channel, check if it has unread content and which filters it belongs to
    for (const channel of allChannels) {
      if (!unreadAffaireIds.has(channel.affaire_id)) continue;

      // Count for 'all' filter (all affaires with notifications)
      counts.all++;

      // Count for 'subscribed' filter
      if (channel.is_subscribed) {
        counts.subscribed++;
      }

      // Count for 'my_affaires' filter
      if (channel.responsable_name && user?.fmName) {
        const channelResponsable = channel.responsable_name.trim().toUpperCase();
        const userFmName = user.fmName.trim().toUpperCase();
        if (channelResponsable === userFmName) {
          counts.my_affaires++;
        }
      }

      // Count for 'a_faire' filter - intersection of unread and todo
      if (todoAffaireIds.has(channel.affaire_id)) {
        counts.a_faire++;
      }
    }

    return counts;
  }, [allChannels, unreadAffaireIds, user, todoAffaireIds]);

  // Filter options for ControlsContainer with notification badges
  // Note: "Toutes les affaires" doesn't show notification count since notifications are always visible in other categories
  const filterOptions = [
    // { key: 'my_affaires', label: 'Mes affaires', notificationCount: filterNotificationCounts.my_affaires }, // Hidden — restore later
    { key: 'a_faire', label: 'À faire', notificationCount: filterNotificationCounts.a_faire },
    { key: 'subscribed', label: 'Abonnés', notificationCount: filterNotificationCounts.subscribed },
    { key: 'all', label: 'Toutes les affaires' },
    { key: 'specific_user', label: 'Affaires de...' }
  ];

  // Handle filter change - reset selectedResponsableName when switching away from specific_user
  // Also reset showClosed to false when LEAVING 'a_faire' (so it's ready in "Ouvert" mode when coming back)
  const handleFilterChange = (value: string) => {
    // Reset showClosed when leaving 'a_faire' tab (before changing the filter)
    if (filters.activeFilter === 'a_faire' && value !== 'a_faire') {
      updateFilter('showClosed', false);
    }
    updateFilter('activeFilter', value);
    if (value !== 'specific_user') {
      updateFilter('selectedResponsableName', null);
    }
  };

  // Handle toggle all status groups
  const handleToggleAllStatusGroups = () => {
    if (filters.visibleStatusGroups.length === 0) {
      // Currently showing all, so collapse to first group only
      updateFilter('visibleStatusGroups', [STATUS_GROUPS[0].id]);
    } else {
      // Currently filtering (some groups selected), so expand to show all
      updateFilter('visibleStatusGroups', []);
    }
  };

  // Handle FileMaker click
  const handleFileMakerClick = (affaireId: number) => {
    const fmUrl = `fmp://$/fm2.chatelainxpert.be/ChatelainXpert.fmp12?script=mondaytofm&param=${affaireId}`;
    window.location.href = fmUrl;
  };

  // Handle subscribe/unsubscribe
  const handleSubscribe = async (channelId: string) => {
    const success = await subscribe(channelId);
    if (success) {
      refetch(); // Refresh the list to update subscription status
    }
  };

  const handleUnsubscribe = async (channelId: string) => {
    const result = await unsubscribe(channelId);

    // If requires confirmation, show dialog and retry with force=true
    if (result.requiresConfirmation) {
      const confirmed = window.confirm(
        result.message ||
          'Vous êtes automatiquement abonné à ce canal car vous êtes responsable de cette affaire.\n\n' +
          'Êtes-vous sûr de vouloir vous désabonner ?'
      );

      if (confirmed) {
        // Retry with force=true
        const forceResult = await unsubscribe(channelId, true);
        if (forceResult.success) {
          refetch(); // Refresh the list to update subscription status
        }
      }
    } else if (result.success) {
      refetch(); // Refresh the list to update subscription status
    }
  };

  // Note: Todo/urgency modal handlers removed - "À faire" is now based on tracker_communication_metadata

  // Handle clear notification (mark all as read)
  const handleClearNotification = async (affaireId: number) => {
    if (clearingNotificationAffaireId) return; // Prevent multiple simultaneous clears

    setClearingNotificationAffaireId(affaireId);
    try {
      const response = await trackerAPI.markAffaireAllRead(affaireId);
      if (response.success) {
        // Refresh unread counts
        refetchUnreadCount();
        // Also refresh the channel list to update unread_task_count
        refetch();
      }
    } catch (error) {
      console.error('Error clearing notification:', error);
    } finally {
      setClearingNotificationAffaireId(null);
    }
  };

  // Handle mark as unread (individual affaire)
  const handleMarkUnread = async (affaireId: number) => {
    if (markingUnreadAffaireId) return;

    setMarkingUnreadAffaireId(affaireId);
    try {
      const response = await trackerAPI.markAffaireAllUnread(affaireId);
      if (response.success) {
        refetchUnreadCount();
        refetch();
      }
    } catch (error) {
      console.error('Error marking as unread:', error);
    } finally {
      setMarkingUnreadAffaireId(null);
    }
  };

  // Handle bulk mark all as read (only affaires with notifications in current filtered list)
  const handleBulkMarkRead = async () => {
    if (isBulkMarkingRead) return;

    // Only affaires with unread content in the current filtered list
    const affaireIds = filteredChannels
      .filter(c => unreadAffaireIds.has(c.affaire_id))
      .map(c => c.affaire_id);

    if (affaireIds.length === 0) return;

    setIsBulkMarkingRead(true);
    try {
      const response = await trackerAPI.bulkMarkAffairesRead(affaireIds);
      if (response.success) {
        refetchUnreadCount();
        refetch();
      }
    } catch (error) {
      console.error('Error bulk marking as read:', error);
    } finally {
      setIsBulkMarkingRead(false);
    }
  };

  // Handle bulk mark all as unread (only affaires without notifications in current filtered list)
  const handleBulkMarkUnread = async () => {
    if (isBulkMarkingUnread) return;

    // Only affaires that are currently read (no notification) in the current filtered list
    const affaireIds = filteredChannels
      .filter(c => !unreadAffaireIds.has(c.affaire_id))
      .map(c => c.affaire_id);

    if (affaireIds.length === 0) return;

    setIsBulkMarkingUnread(true);
    try {
      const response = await trackerAPI.bulkMarkAffairesUnread(affaireIds);
      if (response.success) {
        refetchUnreadCount();
        refetch();
      }
    } catch (error) {
      console.error('Error bulk marking as unread:', error);
    } finally {
      setIsBulkMarkingUnread(false);
    }
  };

  // Count affaires with/without notifications in current filtered list
  const unreadInListCount = useMemo(() => {
    return filteredChannels.filter(c => unreadAffaireIds.has(c.affaire_id)).length;
  }, [filteredChannels, unreadAffaireIds]);

  const readInListCount = useMemo(() => {
    return filteredChannels.filter(c => !unreadAffaireIds.has(c.affaire_id)).length;
  }, [filteredChannels, unreadAffaireIds]);

  if (isLoading) {
    return (
      <div className="tracker-page">
        <Loading
          size="large"
          text="Chargement des channels..."
          fullScreen={false}
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="tracker-page">
        <div className="tracker-error-message">
          {error instanceof Error ? error.message : 'Une erreur est survenue'}
        </div>
      </div>
    );
  }

  return (
    <div className="tracker-page">
      {/* Header with title and stats */}
      <div className="tracker-page__header">
        <div className="tracker-page__header-content">
          <h1 className="tracker-page__title">Tracker de Communications</h1>
          <p className="tracker-page__subtitle">
            Suivez toutes les communications et activités de vos affaires
          </p>
        </div>

        <div className="tracker-page__actions">
          {/* Search mode indicator */}
          {isSearchMode && (
            <span className="tracker-stat tracker-stat--search">
              {isSearching ? (
                <>
                  <svg className="tracker-stat__spinner" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                  </svg>
                  Recherche...
                </>
              ) : (
                <>Recherche base complète</>
              )}
            </span>
          )}
          <span className="tracker-stat">
            Affichées: <strong>{filteredChannels.length}</strong>
            {!isSearchMode && pagination && ` / ${pagination.total}`}
          </span>
          {!isSearchMode && criticalCount > 0 && (
            <span className="tracker-stat tracker-stat--critical">
              Critiques: <strong>{criticalCount}</strong>
            </span>
          )}
          {!isSearchMode && warningCount > 0 && (
            <span className="tracker-stat tracker-stat--warning">
              Avertissements: <strong>{warningCount}</strong>
            </span>
          )}
        </div>
      </div>


      {/* Content with controls and list */}
      <div className="tracker-page__content">
        {/* Integrated Controls Container with Status Filter */}
        <ControlsContainer
          searchQuery={filters.searchQuery}
          onSearchChange={(value) => updateFilter('searchQuery', value)}
          searchPlaceholder="Rechercher par nom, adresse, ID affaire..."
          sortBy={filters.sortBy}
          onSortChange={(value) => updateFilter('sortBy', value)}
          sortOrder={filters.sortOrder}
          onSortOrderChange={(value) => updateFilter('sortOrder', value)}
          sortOptions={sortOptions}
          activeFilter={filters.activeFilter}
          onFilterChange={handleFilterChange}
          filterOptions={filterOptions}
          showFilters={true}
          resultsCount={filteredChannels.length}
          className="tracker-controls"
          filtersDisabled={isSearchMode}
          afterFilters={
            <>
              {/* Unread filter toggle */}
              <div className="tracker-unread-filter">
                <ToggleSwitch
                  options={[
                    { value: 'all', label: 'Tous' },
                    { value: 'unread', label: 'Non lu' }
                  ]}
                  value={filters.showUnreadOnly ? 'unread' : 'all'}
                  onChange={(val) => updateFilter('showUnreadOnly', val === 'unread')}
                  size="small"
                />
              </div>
              {/* Status filter header controls after filter tabs */}
              <StatusGroupFilterHeader
                visibleGroups={filters.visibleStatusGroups}
                totalChannels={channelsBeforeStatusFilter.length}
                totalVisibleChannels={totalVisibleChannels}
                isExpanded={statusFilterExpanded}
                onToggle={() => setStatusFilterExpanded(!statusFilterExpanded)}
                onToggleAll={handleToggleAllStatusGroups}
              />
              {/* Bulk Actions */}
              {filteredChannels.length > 0 && (
                <div className="tracker-bulk-actions">
                  <button
                    onClick={handleBulkMarkRead}
                    disabled={isBulkMarkingRead || unreadInListCount === 0}
                    className="tracker-bulk-btn tracker-bulk-btn--read"
                    title={unreadInListCount > 0 ? `Marquer ${unreadInListCount} affaire(s) comme lu` : 'Aucune affaire non lue'}
                  >
                    {isBulkMarkingRead ? (
                      <svg className="tracker-bulk-btn__spinner" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                      </svg>
                    ) : (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                    <span>Tout marquer lu</span>
                    {unreadInListCount > 0 && <span className="tracker-bulk-btn__count">({unreadInListCount})</span>}
                  </button>
                  <button
                    onClick={handleBulkMarkUnread}
                    disabled={isBulkMarkingUnread || readInListCount === 0}
                    className="tracker-bulk-btn tracker-bulk-btn--unread"
                    title={readInListCount > 0 ? `Marquer ${readInListCount} affaire(s) comme non lu` : 'Aucune affaire lue'}
                  >
                    {isBulkMarkingUnread ? (
                      <svg className="tracker-bulk-btn__spinner" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                      </svg>
                    ) : (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10" />
                      </svg>
                    )}
                    <span>Tout marquer non lu</span>
                    {readInListCount > 0 && <span className="tracker-bulk-btn__count">({readInListCount})</span>}
                  </button>
                </div>
              )}
            </>
          }
        >
          {/* Status Group Filter List (inside controls container) */}
          <StatusGroupFilter
            visibleGroups={filters.visibleStatusGroups}
            onVisibleGroupsChange={(groupIds) => updateFilter('visibleStatusGroups', groupIds)}
            statusSortOrder={filters.statusSortOrder}
            onStatusSortOrderChange={(order) => updateFilter('statusSortOrder', order)}
            channelsByGroup={channelsByGroup}
            isExpanded={statusFilterExpanded}
            onToggleExpanded={() => setStatusFilterExpanded(!statusFilterExpanded)}
            onToggleAll={handleToggleAllStatusGroups}
            disabled={isSearchMode}
          />
        </ControlsContainer>

        {/* Responsable selector - shown when "Affaires de..." is selected (hidden in search mode) */}
        {!isSearchMode && filters.activeFilter === 'specific_user' && (
          <div className="tracker-responsable-selector">
            <label htmlFor="responsable-select" className="tracker-responsable-selector__label">
              Sélectionner un responsable:
            </label>
            <select
              id="responsable-select"
              className="tracker-responsable-selector__select"
              value={filters.selectedResponsableName || ''}
              onChange={(e) => updateFilter('selectedResponsableName', e.target.value || null)}
            >
              <option value="">-- Choisir un responsable --</option>
              {uniqueResponsables.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Actions Bar - Open/Closed Toggle for "À faire" filter (hidden in search mode) */}
        {!isSearchMode && filters.activeFilter === 'a_faire' && (
          <div className="tracker-actions-bar">
            <div className="tracker-open-closed-toggle">
              <button
                onClick={() => updateFilter('showClosed', false)}
                className={`tracker-toggle-btn ${!filters.showClosed ? 'active' : ''}`}
              >
                Ouvert
              </button>
              <button
                onClick={() => updateFilter('showClosed', true)}
                className={`tracker-toggle-btn ${filters.showClosed ? 'active' : ''}`}
              >
                Terminé
              </button>
            </div>
          </div>
        )}

        {/* Channel List */}
        <div className="tracker-channels-grid">
          {filteredChannels.length === 0 ? (
            <div className="tracker-empty-state">
              <h3>{filters.searchQuery || filters.showUnreadOnly ? 'Aucun résultat' : 'Aucun channel'}</h3>
              {filters.searchQuery && (
                <p>Essayez de modifier votre recherche</p>
              )}
              {filters.showUnreadOnly && !filters.searchQuery && (
                <p>Aucune affaire non lue</p>
              )}
            </div>
          ) : (
            filteredChannels.map((channel) => {
              // Get rule evaluations for this channel
              const channelRules = ruleEvaluations.get(channel.id) || [];

              return (
                <TrackerChannelCard
                  key={channel.id}
                  channel={channel}
                  currentUserId={user?.id}
                  onFileMaker={() => handleFileMakerClick(channel.affaire_id)}
                  onSubscribe={() => handleSubscribe(channel.id)}
                  onUnsubscribe={() => handleUnsubscribe(channel.id)}
                  isSubscribing={subscriptionLoading}
                  ruleEvaluations={channelRules}
                  hasNotification={unreadAffaireIds.has(channel.affaire_id)}
                  onClearNotification={() => handleClearNotification(channel.affaire_id)}
                  isClearingNotification={clearingNotificationAffaireId === channel.affaire_id}
                  onMarkUnread={() => handleMarkUnread(channel.affaire_id)}
                  isMarkingUnread={markingUnreadAffaireId === channel.affaire_id}
                  isHighlighted={lastClickedAffaireId === channel.affaire_id}
                />
              );
            })
          )}
        </div>
      </div>
      {/* Note: Todo/Urgency modals removed - "À faire" is now based on tracker_communication_metadata */}
    </div>
  );
};

export default TrackerTab;
